import cloneDeep from "lodash/cloneDeep";

import { fillPersonalization } from "@/modules/persona";
import {
  ConsumptionDatum,
  getConsumptionFromProfile,
} from "@/modules/persona/consumption";
import { computeIntermediateValues } from "@/modules/persona/consumption/computing";
import { ProfileToEnergyConsumptionData } from "@/modules/play/context/playContext";
import {
  carAges,
  carEnergies,
  houseEnergies,
  houseLighting,
  hygieneCleaning,
  IntermediateValues,
  PersoForm,
  showerTimes,
} from "@/modules/play/Personalization/models/form";
import { availableActions } from "@/modules/play/playerActions/constants/actions";
import { ActionNames } from "@/utils/types";

export {
  computeNewConsumptionData,
  isDirectEnergyConsumption,
  isFossilEnergyConsumption,
  isGreyEnergyConsumption,
};

interface ImpactMatrix {
  personalization: PersoForm;
  calculatedValues?: IntermediateValues;
}

const consumptionConfig: {
  [k in ActionNames]: ConsumptionConfig;
} = {
  [availableActions.REDUCE_PLANE_HALF]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "planeDistance",
        update: (value: number) => value * 0.5,
      }),
    ],
  },
  [availableActions.TRANSPORT_STOP_PLANE]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "planeDistance",
        update: (value: number) => 0,
      }),
    ],
  },
  [availableActions.LOCAL_CONSUMPTION]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingLocal",
        update: (value: boolean) => true,
      }),
    ],
  },
  [availableActions.REDUCE_CLOTHING_HALF]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "clothingQuantity",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.REDUCE_NUMERIC]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "numericEquipment",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.STOP_MILK]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingDairies",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.STOP_EGGS]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingEggs",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.STOP_MEAT]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingMeat",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.ZERO_WASTE]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingZeroWaste",
        update: (value: boolean) => true,
      }),
    ],
  },
  [availableActions.REDUCE_TRAIN_HALF]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "trainDistance",
        update: (value: number) => value * 0.5,
      }),
    ],
  },
  [availableActions.DIGITAL_REDUCE_INTERNET_HALF]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "numericWebTimeDay",
        update: (value: boolean) => false,
      }),
      () => ({
        type: "personalization",
        name: "numericVideoTimeDay",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.HOUSE_INSTALL_EFFICIENT_ELECTRIC_WOOD_HEATING_SYSTEM]: {
    order: 1,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_EFFICIENT_ELECTRIC_WOOD_HEATING_SYSTEM_ON_HEATING_CONSUMPTION_COEF,
      }),
      ({ profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_EFFICIENT_ELECTRIC_WOOD_HEATING_SYSTEM_ON_HEATING_CONSUMPTION_COEF,
      }),
      () => ({
        type: "personalization",
        name: "heatingEnergy",
        update: (value: string) => houseEnergies.ELECTRICITE.value,
      }),
    ],
  },
  [availableActions.HOUSE_INSULATE_WALLS]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_INSULATING_WALLS_ON_HEATING_CONSUMPTION_COEF,
      }),
      ({ profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_INSULATING_WALLS_ON_HEATING_CONSUMPTION_COEF,
      }),
    ],
  },
  [availableActions.HOUSE_INSULATE_ROOF]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_INSULATING_ROOF_ON_HEATING_CONSUMPTION_COEF,
      }),
      ({ profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_INSULATING_ROOF_ON_HEATING_CONSUMPTION_COEF,
      }),
    ],
  },
  [availableActions.HOUSE_CHANGE_WOODWORK_WINDOWS]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_CHANGING_WOODWORK_WINDOWS_ON_HEATING_CONSUMPTION_COEF,
      }),
      ({ profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_CHANGING_WOODWORK_WINDOWS_ON_HEATING_CONSUMPTION_COEF,
      }),
    ],
  },
  [availableActions.HOUSE_INSTALL_EFFICIENT_VENTILATION_SYSTEM]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_EFFICIENT_VENTILATION_SYSTEM_ON_HEATING_CONSUMPTION_COEF,
      }),
      ({ profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_EFFICIENT_VENTILATION_SYSTEM_ON_HEATING_CONSUMPTION_COEF,
      }),
    ],
  },
  [availableActions.HOUSE_19_DEGREES_MAX]: {
    order: 2,
    profileImpacts: [
      ({ inputMatrix, profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) =>
          inputMatrix.personalization.heatingTemperature
            ? value *
              profileToEnergyConsumptionData.IMPACT_OF_HEATING_HOUSE_19_DEGREES_MAX_ON_HEATING_CONSUMPTION_COEF
            : value,
      }),
      ({ inputMatrix, profileToEnergyConsumptionData }) => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) =>
          inputMatrix.personalization.heatingTemperature
            ? value *
              profileToEnergyConsumptionData.IMPACT_OF_HEATING_HOUSE_19_DEGREES_MAX_ON_HEATING_CONSUMPTION_COEF
            : value,
      }),
    ],
  },
  [availableActions.HOUSE_REDUCE_SIZE_HALF]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "calculatedValues",
        name: "heatingConsumptionInvoiceCoeff",
        update: (value) => value * 0.5,
      }),
      () => ({
        type: "personalization",
        name: "heatingConsumption",
        update: (value: number) => value * 0.5,
      }),
    ],
  },
  [availableActions.HOUSE_STOP_AIR_CONDITIONING]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "airConditionning",
        update: (value: boolean) => false,
      }),
    ],
  },
  [availableActions.HOUSE_ONLY_LEDS]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "lightingSystem",
        update: (value: string) => houseLighting.AMPOULES_LED.value,
      }),
    ],
  },
  [availableActions.HOUSE_ONE_SHOWER_5_MINUTES_MAX]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "showerBath",
        update: (value: string) => hygieneCleaning.DOUCHES.value,
      }),
      () => ({
        type: "personalization",
        name: "showerNumber",
        update: (value: number) => 1,
      }),
      () => ({
        type: "personalization",
        name: "showerTime",
        update: (value: string) => showerTimes.MOINS_5.value,
      }),
    ],
  },
  [availableActions.HOUSE_EFFICIENT_HOUSEHOLD_APPLIANCES]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "whiteProductsCoeff",
        update: (value) =>
          value *
          profileToEnergyConsumptionData.IMPACT_OF_EFFICIENT_HOUSEHOLD_APPLIANCES_ON_WHITE_GOODS_CONSUMPTION_COEF,
      }),
    ],
  },
  [availableActions.HOUSE_UNPLUNG_APPLIANCES_ON_STANDBY]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "brownGoodsConsumption",
        update: (value) =>
          value +
          profileToEnergyConsumptionData.IMPACT_OF_UNPLUGGING_APPLIANCES_ON_STANDBY_ON_BROWN_GOODS_CONSUMPTION_TERM,
      }),
    ],
  },
  [availableActions.HOUSE_UNPLUNG_CHARGERS]: {
    order: 2,
    profileImpacts: [
      ({ profileToEnergyConsumptionData }) => ({
        type: "calculatedValues",
        name: "brownGoodsConsumption",
        update: (value) =>
          value +
          profileToEnergyConsumptionData.IMPACT_OF_UNPLUGGING_CHARGERS_ON_BROWN_GOODS_CONSUMPTION_TERM,
      }),
    ],
  },
  [availableActions.STOP_CANS]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "eatingTinDrink",
        update: (value: number) => 0,
      }),
    ],
  },
  [availableActions.CONSUMPTION_SORT_WASTE]: {
    order: 1,
    profileImpacts: [],
  },
  [availableActions.ELECTRIC_CAR]: {
    order: 1,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "carEnergy",
        update: (value: string) => carEnergies.ELECTRICITE.value,
      }),
    ],
  },
  [availableActions.ECO_DRIVING]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "carDistanceAlone",
        update: (value: number) => value * 0.9,
      }),
      () => ({
        type: "personalization",
        name: "carDistanceCarsharing",
        update: (value: number) => value * 0.9,
      }),
      () => ({
        type: "personalization",
        name: "carDistanceHoushold",
        update: (value: number) => value * 0.9,
      }),
    ],
  },
  [availableActions.REDUCE_CAR_20]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "carDistanceAlone",
        update: (value: number) => value * 0.8,
      }),
      () => ({
        type: "personalization",
        name: "carDistanceCarsharing",
        update: (value: number) => value * 0.8,
      }),
      () => ({
        type: "personalization",
        name: "carDistanceHoushold",
        update: (value: number) => value * 0.8,
      }),
    ],
  },
  [availableActions.KEEP_CAR_15]: {
    order: 2,
    profileImpacts: [
      () => ({
        type: "personalization",
        name: "carAge",
        update: (value: string) => carAges.PLUS_15.value,
      }),
    ],
  },
};

type ConsumptionConfig = {
  order: number;
  profileImpacts: ConsumptionUpdate[];
};

type ConsumptionUpdate<
  TType extends "calculatedValues" | "personalization" =
    | "calculatedValues"
    | "personalization",
> = (params: {
  inputMatrix: ImpactMatrix;
  profileToEnergyConsumptionData: ProfileToEnergyConsumptionData;
}) => TType extends "calculatedValues"
  ? {
      type: "calculatedValues";
      name: keyof IntermediateValues;
      update: (value: number) => number;
    }
  : {
      type: "personalization";
      name: keyof PersoForm;
      update:
        | ((value: boolean) => boolean)
        | ((value: number) => number)
        | ((value: string) => string);
    };

function computeNewConsumptionData(
  performedActionsNames: ActionNames[],
  personalization: PersoForm,
  profileToEnergyConsumptionData: ProfileToEnergyConsumptionData,
) {
  const newPersonalization = cloneDeep(personalization);

  computeConsumption(
    { personalization: newPersonalization },
    performedActionsNames,
    "personalization",
    profileToEnergyConsumptionData,
  );
  const fullPersonalization = fillPersonalization(newPersonalization);

  const intermediateValues = computeIntermediateValues(
    fullPersonalization,
    profileToEnergyConsumptionData,
  );
  computeConsumption(
    {
      personalization: newPersonalization,
      calculatedValues: intermediateValues,
    },
    performedActionsNames,
    "calculatedValues",
    profileToEnergyConsumptionData,
  );

  return getConsumptionFromProfile(
    fullPersonalization,
    intermediateValues,
    profileToEnergyConsumptionData,
  );
}

function computeConsumption(
  inputMatrix: ImpactMatrix,
  performedActionsNames: ActionNames[],
  impactType: keyof ImpactMatrix,
  profileToEnergyConsumptionData: ProfileToEnergyConsumptionData,
) {
  const performedActionsNamesSorted = performedActionsNames.sort(
    (actionNameA, actionNameB) =>
      (consumptionConfig[actionNameA]?.order || 0) -
      (consumptionConfig[actionNameB]?.order || 0),
  );

  performedActionsNamesSorted.forEach((actionName) => {
    const config = consumptionConfig[actionName];

    if (!config) {
      return;
    }

    config.profileImpacts.forEach((getImpact) => {
      const { type } = getImpact({
        inputMatrix,
        profileToEnergyConsumptionData,
      });
      if (impactType === type) {
        applyPersonalizationImpact(getImpact, {
          inputMatrix,
          profileToEnergyConsumptionData,
        });
      }
    });
  });
}

function applyPersonalizationImpact(
  getImpact: ConsumptionUpdate,
  params: {
    inputMatrix: ImpactMatrix;
    profileToEnergyConsumptionData: ProfileToEnergyConsumptionData;
  },
) {
  const { type, name, update } = getImpact(params);
  if (type === "calculatedValues" && params.inputMatrix.calculatedValues) {
    if (params.inputMatrix.calculatedValues[name] == null) {
      return;
    }

    Object.assign(params.inputMatrix.calculatedValues, {
      [name]: update(params.inputMatrix.calculatedValues[name]),
    });
  }

  if (type === "personalization") {
    if (params.inputMatrix.personalization[name] == null) {
      return;
    }

    Object.assign(params.inputMatrix.personalization, {
      [name]: (update as any)(params.inputMatrix.personalization[name]),
    });
  }
}

function isDirectEnergyConsumption(consumption: ConsumptionDatum): boolean {
  return consumption.type !== "grey";
}

function isFossilEnergyConsumption(consumption: ConsumptionDatum): boolean {
  return consumption.type === "fossil";
}

function isGreyEnergyConsumption(consumption: ConsumptionDatum): boolean {
  return consumption.type === "grey";
}
